import React from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .wd-page * { box-sizing: border-box; margin: 0; padding: 0; }
  .wd-page { font-family: 'Inter', sans-serif; color: #0f172a; background: #ffffff; width: 100%; }

  /* ── Hero ── */
  .wd-hero { position: relative; padding: 100px 24px 120px; overflow: hidden; background: #fff; }
  .wd-hero-blob1 { position: absolute; top: -8%; right: -5%; width: 600px; height: 600px; border-radius: 50%; background: radial-gradient(circle, rgba(109,40,217,0.38), rgba(14,165,233,0.18)); filter: blur(90px); pointer-events: none; }
  .wd-hero-blob2 { position: absolute; bottom: -14%; left: -8%; width: 660px; height: 660px; border-radius: 50%; background: radial-gradient(circle, rgba(14,165,233,0.28), rgba(109,40,217,0.14)); filter: blur(90px); pointer-events: none; }
  .wd-hero-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 72px; align-items: center; position: relative; z-index: 1; }
  @media (max-width: 900px) { .wd-hero-inner { grid-template-columns: 1fr; } .wd-hero-visual { display: none; } }

  .wd-badge { display: inline-flex; align-items: center; gap: 8px; padding: 8px 18px; border-radius: 999px; background: #f5f3ff; border: 1px solid #ddd6fe; font-size: 13px; font-weight: 600; color: #5b21b6; margin-bottom: 28px; }
  .wd-badge-dot { width: 8px; height: 8px; border-radius: 50%; background: #7c3aed; display: inline-block; animation: wd-pulse 2s ease-in-out infinite; }
  @keyframes wd-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(124,58,237,0.5); } 50% { box-shadow: 0 0 0 6px rgba(124,58,237,0); } }

  .wd-hero h1 { font-size: clamp(1.95rem, 3.75vw, 3.15rem); font-weight: 900; line-height: 1.08; letter-spacing: -0.03em; color: #0f172a; margin-bottom: 24px; }
  .wd-gradient-text { background: linear-gradient(135deg, #6d28d9 0%, #0ea5e9 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .wd-hero-sub { font-size: 1.15rem; color: #475569; line-height: 1.8; margin-bottom: 40px; font-weight: 400; max-width: 540px; }
  .wd-btn-group { display: flex; gap: 16px; flex-wrap: wrap; }
  .wd-btn-primary { padding: 14px 28px; background: #6d28d9; color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, transform 0.15s; box-shadow: 0 4px 18px rgba(109,40,217,0.35); }
  .wd-btn-primary:hover { background: #7c3aed; transform: translateY(-1px); }
  .wd-btn-secondary { padding: 14px 28px; background: #fff; color: #334155; border: 1px solid #e2e8f0; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .wd-btn-secondary:hover { border-color: #c4b5fd; background: #f5f3ff; color: #6d28d9; }

  /* Hero Browser Mockup */
  .wd-browser { background: #1e1b4b; border-radius: 18px; overflow: hidden; box-shadow: 0 32px 80px rgba(15,23,42,0.35), 0 4px 16px rgba(109,40,217,0.18); }
  .wd-browser-bar { background: #312e81; padding: 12px 16px; display: flex; align-items: center; gap: 10px; }
  .wd-browser-dots { display: flex; gap: 6px; }
  .wd-browser-dot { width: 12px; height: 12px; border-radius: 50%; }
  .wd-browser-dot.red   { background: #f87171; }
  .wd-browser-dot.yellow { background: #fbbf24; }
  .wd-browser-dot.green { background: #34d399; }
  .wd-browser-url { flex: 1; background: rgba(255,255,255,0.08); border-radius: 8px; padding: 6px 14px; font-size: 12px; color: rgba(255,255,255,0.55); font-family: monospace; margin: 0 10px; }
  .wd-browser-body { padding: 24px; display: flex; flex-direction: column; gap: 14px; }
  .wd-code-block { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 16px 18px; }
  .wd-code-line { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
  .wd-code-line:last-child { margin-bottom: 0; }
  .wd-code-indent { width: 18px; flex-shrink: 0; }
  .wd-code-tag { font-size: 12px; font-family: monospace; color: #818cf8; }
  .wd-code-attr { font-size: 12px; font-family: monospace; color: #34d399; }
  .wd-code-val { font-size: 12px; font-family: monospace; color: #f59e0b; }
  .wd-metric-row { display: flex; gap: 10px; }
  .wd-metric-chip { flex: 1; background: rgba(109,40,217,0.18); border: 1px solid rgba(109,40,217,0.3); border-radius: 10px; padding: 12px 14px; text-align: center; }
  .wd-metric-chip.cyan { background: rgba(14,165,233,0.14); border-color: rgba(14,165,233,0.3); }
  .wd-metric-chip.green { background: rgba(52,211,153,0.14); border-color: rgba(52,211,153,0.3); }
  .wd-mc-val { font-size: 20px; font-weight: 900; color: #fff; letter-spacing: -0.02em; }
  .wd-mc-label { font-size: 10.5px; color: rgba(255,255,255,0.5); margin-top: 3px; }
  .wd-deploy-banner { background: linear-gradient(90deg, rgba(109,40,217,0.22), rgba(14,165,233,0.16)); border: 1px solid rgba(109,40,217,0.3); border-radius: 12px; padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; }
  .wd-deploy-text { font-size: 12px; color: rgba(255,255,255,0.75); font-family: monospace; }
  .wd-deploy-badge { background: rgba(52,211,153,0.2); border: 1px solid rgba(52,211,153,0.4); border-radius: 999px; padding: 4px 12px; font-size: 11px; font-weight: 700; color: #34d399; }

  /* floating badge */
  .wd-float-badge { position: absolute; bottom: -20px; left: -20px; background: #fff; border-radius: 16px; padding: 14px 18px; box-shadow: 0 16px 48px rgba(15,23,42,0.18); display: flex; align-items: center; gap: 12px; }
  .wd-fb-icon { width: 42px; height: 42px; background: #f5f3ff; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 19px; flex-shrink: 0; }
  .wd-fb-label { font-size: 11px; color: #94a3b8; font-weight: 500; }
  .wd-fb-value { font-size: 15px; font-weight: 800; color: #0f172a; margin-top: 2px; }

  /* ── Stats Bar ── */
  .wd-stats { background: #fff; border-top: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9; }
  .wd-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); max-width: 1200px; margin: 0 auto; }
  @media (max-width: 768px) { .wd-stats-grid { grid-template-columns: 1fr 1fr; } }
  .wd-stat-item { padding: 38px 24px; text-align: center; border-right: 1px solid #f1f5f9; }
  .wd-stat-item:last-child { border-right: none; }
  @media (max-width: 768px) { .wd-stat-item:nth-child(2n) { border-right: none; } .wd-stat-item { border-bottom: 1px solid #f1f5f9; } }
  .wd-stat-num { font-size: 2.2rem; font-weight: 900; color: #6d28d9; letter-spacing: -0.03em; line-height: 1; margin-bottom: 8px; }
  .wd-stat-label { font-size: 0.82rem; color: #64748b; font-weight: 500; line-height: 1.4; }

  /* ── Shared Section Atoms ── */
  .wd-section { padding: 96px 24px; }
  .wd-container { max-width: 1200px; margin: 0 auto; }
  .wd-section-header { text-align: center; max-width: 720px; margin: 0 auto 64px; }
  .wd-section-header h2 { font-size: clamp(1.8rem, 3.5vw, 2.8rem); font-weight: 800; color: #0f172a; letter-spacing: -0.02em; margin-bottom: 16px; line-height: 1.2; }
  .wd-section-header p { font-size: 1.06rem; color: #64748b; line-height: 1.8; }
  .wd-kicker { font-size: 12.5px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #6d28d9; margin-bottom: 12px; }

  /* ── Services Grid ── */
  .wd-services-bg { background: #faf9ff; border-top: 1px solid rgba(221,214,254,0.5); border-bottom: 1px solid rgba(221,214,254,0.5); }
  .wd-services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  @media (max-width: 960px) { .wd-services-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .wd-services-grid { grid-template-columns: 1fr; } }
  .wd-svc-card { background: #fff; padding: 36px 32px; border-radius: 20px; border: 1px solid rgba(221,214,254,0.6); box-shadow: 0 1px 4px rgba(0,0,0,0.03); transition: all 0.3s; position: relative; overflow: hidden; cursor: pointer; }
  .wd-svc-card:hover { box-shadow: 0 18px 52px rgba(109,40,217,0.1); transform: translateY(-3px); border-color: transparent; }
  .wd-svc-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; opacity: 0; transition: opacity 0.3s; border-radius: 20px 20px 0 0; }
  .wd-svc-card:hover::before { opacity: 1; }
  .wd-svc-card.c-violet::before  { background: linear-gradient(90deg, #6d28d9, #7c3aed); }
  .wd-svc-card.c-cyan::before    { background: linear-gradient(90deg, #0ea5e9, #06b6d4); }
  .wd-svc-card.c-indigo::before  { background: linear-gradient(90deg, #4f46e5, #6d28d9); }
  .wd-svc-card.c-teal::before    { background: linear-gradient(90deg, #0d9488, #0ea5e9); }
  .wd-svc-card.c-fuchsia::before { background: linear-gradient(90deg, #a21caf, #6d28d9); }
  .wd-svc-card.c-emerald::before { background: linear-gradient(90deg, #059669, #0d9488); }
  .wd-svc-icon { width: 56px; height: 56px; border-radius: 14px; display: flex; align-items: center; justify-content: center; margin-bottom: 22px; font-size: 24px; }
  .wd-svc-icon.c-violet  { background: #f5f3ff; }
  .wd-svc-icon.c-cyan    { background: #e0f2fe; }
  .wd-svc-icon.c-indigo  { background: #eef2ff; }
  .wd-svc-icon.c-teal    { background: #f0fdfa; }
  .wd-svc-icon.c-fuchsia { background: #fdf4ff; }
  .wd-svc-icon.c-emerald { background: #ecfdf5; }
  .wd-svc-card h3 { font-size: 1.1rem; font-weight: 700; color: #1e293b; margin-bottom: 12px; }
  .wd-svc-card p { font-size: 0.91rem; color: #64748b; line-height: 1.72; margin-bottom: 22px; }
  .wd-svc-tags { display: flex; flex-wrap: wrap; gap: 7px; }
  .wd-svc-tag { padding: 4px 11px; font-size: 11px; font-weight: 600; border-radius: 999px; background: rgba(226,232,240,0.6); color: #475569; transition: all 0.2s; }
  .wd-svc-card:hover .wd-svc-tag { background: #f5f3ff; color: #6d28d9; }
  .wd-svc-arrow { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #6d28d9; margin-top: 16px; opacity: 0; transform: translateX(-4px); transition: opacity 0.2s, transform 0.2s; }
  .wd-svc-card:hover .wd-svc-arrow { opacity: 1; transform: translateX(0); }

  /* ── Feature Spotlight ── */
  .wd-spotlight-bg { background: #fff; }
  .wd-spotlight-inner { background: #faf9ff; border: 1px solid rgba(221,214,254,0.7); border-radius: 32px; padding: 72px 64px; display: flex; gap: 80px; align-items: center; flex-wrap: wrap; }
  @media (max-width: 960px) { .wd-spotlight-inner { padding: 48px 40px; gap: 48px; } }
  .wd-spotlight-left { flex: 1; min-width: 280px; }
  .wd-spotlight-left h2 { font-size: clamp(1.8rem, 3vw, 2.5rem); font-weight: 800; color: #0f172a; letter-spacing: -0.02em; margin-bottom: 14px; line-height: 1.22; }
  .wd-spotlight-left > p { font-size: 1.03rem; color: #64748b; line-height: 1.8; margin-bottom: 44px; }
  .wd-feat-list { display: flex; flex-direction: column; gap: 28px; }
  .wd-feat { display: flex; gap: 16px; align-items: flex-start; }
  .wd-feat-icon { flex-shrink: 0; width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 20px; }
  .wd-feat-icon.c-violet { background: #f5f3ff; }
  .wd-feat-icon.c-cyan   { background: #e0f2fe; }
  .wd-feat-icon.c-teal   { background: #f0fdfa; }
  .wd-feat h4 { font-size: 1rem; font-weight: 700; color: #1e293b; margin-bottom: 5px; }
  .wd-feat p { font-size: 0.9rem; color: #64748b; line-height: 1.68; }
  .wd-spotlight-right { flex: 1; min-width: 280px; }
  .wd-visual-card { background: linear-gradient(145deg, #1e1b4b, #312e81); border-radius: 24px; padding: 36px; position: relative; overflow: hidden; min-height: 390px; display: flex; flex-direction: column; gap: 20px; justify-content: space-between; }
  .wd-visual-dots { position: absolute; inset: 0; opacity: 0.1; background-image: radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 0); background-size: 26px 26px; pointer-events: none; }
  .wd-visual-metric-box { background: rgba(255,255,255,0.12); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.2); border-radius: 18px; padding: 22px; }
  .wd-vm-label { font-size: 12px; color: rgba(255,255,255,0.65); font-weight: 500; margin-bottom: 6px; }
  .wd-vm-value { font-size: 34px; font-weight: 900; color: #fff; letter-spacing: -0.03em; line-height: 1; }
  .wd-vm-delta { font-size: 12px; color: rgba(255,255,255,0.8); margin-top: 6px; display: flex; align-items: center; gap: 4px; }
  .wd-vm-delta span { background: rgba(255,255,255,0.18); border-radius: 999px; padding: 2px 8px; font-weight: 600; }
  .wd-visual-insight { background: #fff; border-radius: 18px; padding: 22px; box-shadow: 0 8px 32px rgba(0,0,0,0.16); }
  .wd-vi-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
  .wd-vi-title { font-weight: 700; color: #1e293b; font-size: 14px; }
  .wd-vi-badge { font-size: 11px; font-weight: 700; color: #6d28d9; background: #f5f3ff; padding: 4px 10px; border-radius: 999px; letter-spacing: 0.04em; }
  .wd-bar-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
  .wd-bar-label { font-size: 11.5px; color: #64748b; width: 82px; flex-shrink: 0; }
  .wd-bar-track { flex: 1; height: 7px; background: #f1f5f9; border-radius: 999px; overflow: hidden; }
  .wd-bar-fill { height: 100%; border-radius: 999px; }
  .wd-bar-val { font-size: 12px; font-weight: 600; color: #334155; width: 32px; text-align: right; }

  /* ── Process Steps ── */
  .wd-process-bg { background: #faf9ff; border-top: 1px solid rgba(221,214,254,0.5); }
  .wd-steps { display: grid; grid-template-columns: repeat(5, 1fr); gap: 28px; position: relative; }
  @media (max-width: 1024px) { .wd-steps { grid-template-columns: repeat(3, 1fr); } }
  @media (max-width: 640px) { .wd-steps { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 420px) { .wd-steps { grid-template-columns: 1fr; } }
  .wd-steps::before { content: ''; position: absolute; top: 32px; left: calc(10% + 8px); right: calc(10% + 8px); height: 2px; background: linear-gradient(90deg, #6d28d9, #0ea5e9); z-index: 0; opacity: 0.25; }
  @media (max-width: 1024px) { .wd-steps::before { display: none; } }
  .wd-step { text-align: center; position: relative; z-index: 1; }
  .wd-step-num { width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg, #6d28d9, #0ea5e9); color: #fff; font-size: 1.35rem; font-weight: 900; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; box-shadow: 0 8px 24px rgba(109,40,217,0.35); }
  .wd-step-chip { font-size: 11px; font-weight: 700; color: #6d28d9; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 10px; }
  .wd-step h3 { font-size: 0.97rem; font-weight: 700; color: #1e293b; margin-bottom: 10px; }
  .wd-step p { font-size: 0.85rem; color: #64748b; line-height: 1.7; max-width: 200px; margin: 0 auto; }

  /* ── Why Us (dark) ── */
  .wd-why-bg { background: #0f172a; color: #f8fafc; position: relative; overflow: hidden; }
  .wd-why-glow1 { position: absolute; top: -8%; right: 18%; width: 600px; height: 600px; background: radial-gradient(circle, rgba(109,40,217,0.22), transparent 70%); pointer-events: none; }
  .wd-why-glow2 { position: absolute; bottom: -15%; left: -6%; width: 520px; height: 520px; background: radial-gradient(circle, rgba(14,165,233,0.16), transparent 70%); pointer-events: none; }
  .wd-why-bg .wd-kicker { color: #a5b4fc; }
  .wd-why-bg h2 { color: #fff; }
  .wd-why-bg .wd-section-header p { color: #94a3b8; }
  .wd-why-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  @media (max-width: 960px) { .wd-why-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .wd-why-grid { grid-template-columns: 1fr; } }
  .wd-why-card { background: rgba(30,27,75,0.55); border: 1px solid rgba(109,40,217,0.22); border-radius: 20px; padding: 32px; transition: border-color 0.3s, background 0.3s; }
  .wd-why-card:hover { border-color: rgba(109,40,217,0.5); background: rgba(30,27,75,0.75); }
  .wd-why-icon-wrap { font-size: 28px; margin-bottom: 18px; display: block; }
  .wd-why-card h4 { font-size: 1.02rem; font-weight: 700; color: #fff; margin-bottom: 10px; }
  .wd-why-card p { font-size: 0.89rem; color: #94a3b8; line-height: 1.72; }

  /* ── Testimonials ── */
  .wd-testimonials-bg { background: #fff; }
  .wd-testimonials-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  @media (max-width: 960px) { .wd-testimonials-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .wd-testimonials-grid { grid-template-columns: 1fr; } }
  .wd-tcard { background: #faf9ff; border: 1px solid rgba(221,214,254,0.5); border-radius: 20px; padding: 32px; transition: all 0.3s; display: flex; flex-direction: column; }
  .wd-tcard:hover { background: #fff; box-shadow: 0 14px 44px rgba(109,40,217,0.09); border-color: #c4b5fd; }
  .wd-stars { color: #f59e0b; font-size: 15px; letter-spacing: 2px; margin-bottom: 18px; }
  .wd-tcard-text { font-size: 0.93rem; color: #334155; line-height: 1.78; font-style: italic; margin-bottom: 24px; flex: 1; }
  .wd-tcard-author { display: flex; align-items: center; gap: 12px; }
  .wd-author-avatar { width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 700; color: #fff; flex-shrink: 0; }
  .wd-author-name { font-size: 13.5px; font-weight: 700; color: #1e293b; }
  .wd-author-role { font-size: 12px; color: #94a3b8; margin-top: 1px; }

  /* ── CTA ── */
  .wd-cta-bg { background: #fff; border-top: 1px solid #f1f5f9; }
  .wd-cta-inner { background: #0f172a; border-radius: 32px; padding: 88px 56px; text-align: center; position: relative; overflow: hidden; max-width: 1000px; margin: 0 auto; }
  @media (max-width: 640px) { .wd-cta-inner { padding: 52px 28px; border-radius: 24px; } }
  .wd-cta-glow1 { position: absolute; top: -20%; right: -5%; width: 420px; height: 420px; background: radial-gradient(circle, rgba(109,40,217,0.32), transparent 65%); pointer-events: none; }
  .wd-cta-glow2 { position: absolute; bottom: -20%; left: -5%; width: 420px; height: 420px; background: radial-gradient(circle, rgba(14,165,233,0.22), transparent 65%); pointer-events: none; }
  .wd-cta-inner h2 { font-size: clamp(2rem, 4vw, 3rem); font-weight: 900; color: #fff; letter-spacing: -0.03em; line-height: 1.15; margin-bottom: 18px; position: relative; z-index: 1; }
  .wd-cta-inner > p { font-size: 1.12rem; color: #94a3b8; font-weight: 300; max-width: 560px; margin: 0 auto 40px; line-height: 1.75; position: relative; z-index: 1; }
  .wd-cta-btns { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; margin-bottom: 32px; }
  .wd-cta-primary { padding: 15px 34px; background: #6d28d9; color: #fff; border: none; border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, transform 0.15s; box-shadow: 0 4px 20px rgba(109,40,217,0.45); }
  .wd-cta-primary:hover { background: #7c3aed; transform: translateY(-1px); }
  .wd-cta-secondary { padding: 15px 34px; background: #1e293b; color: #cbd5e1; border: 1px solid #334155; border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
  .wd-cta-secondary:hover { background: #334155; color: #fff; }
  .wd-cta-trust { display: flex; align-items: center; justify-content: center; gap: 28px; flex-wrap: wrap; position: relative; z-index: 1; }
  .wd-cta-trust-item { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #64748b; }
  .wd-cta-trust-item .check { color: #818cf8; font-weight: 800; }

  /* ── Service Sub-Page Hero ── */
  .wd-svchero { position: relative; padding: 80px 24px 100px; overflow: hidden; background: #fff; }
  .wd-svchero-blob1 { position: absolute; top: -10%; right: -5%; width: 500px; height: 500px; border-radius: 50%; background: radial-gradient(circle, rgba(109,40,217,0.38), rgba(14,165,233,0.16)); filter: blur(70px); pointer-events: none; }
  .wd-svchero-blob2 { position: absolute; bottom: -15%; left: -6%; width: 560px; height: 560px; border-radius: 50%; background: radial-gradient(circle, rgba(14,165,233,0.26), rgba(109,40,217,0.13)); filter: blur(70px); pointer-events: none; }
  .wd-svchero-inner { max-width: 1100px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; position: relative; z-index: 1; }
  @media (max-width: 900px) { .wd-svchero-inner { grid-template-columns: 1fr; } .wd-svchero-card { display: none; } }
  .wd-svchero-card { background: #fff; border-radius: 24px; border: 1px solid #e2e8f0; padding: 28px; box-shadow: 0 20px 60px rgba(109,40,217,0.1), 0 4px 16px rgba(0,0,0,0.04); }
  .wd-svchero-card-hdr { display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px; padding-bottom: 14px; border-bottom: 1px solid #f1f5f9; }
  .wd-svchero-hdr-text { font-size: 15px; font-weight: 700; color: #0f172a; }
  .wd-svchero-hdr-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }
  .wd-svchero-hdr-icon { width: 42px; height: 42px; background: #f5f3ff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 18px; }
  .wd-svchero-row { display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; border-radius: 10px; background: #faf9ff; border: 1px solid rgba(221,214,254,0.5); margin-bottom: 8px; }
  .wd-svchero-row-label { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: #475569; font-weight: 500; }
  .wd-svchero-row-val { font-size: 12.5px; font-weight: 700; color: #6d28d9; }
  .wd-svchero-metric { margin-top: 14px; padding: 18px 20px; background: linear-gradient(135deg, #6d28d9, #0ea5e9); border-radius: 14px; display: flex; justify-content: space-between; align-items: center; }
  .wd-svchero-metric-label { font-size: 11px; color: rgba(255,255,255,0.8); font-weight: 500; margin-bottom: 3px; }
  .wd-svchero-metric-value { font-size: 22px; font-weight: 900; color: #fff; letter-spacing: -0.02em; }
  .wd-svchero-metric-badge { background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25); border-radius: 999px; padding: 4px 10px; font-size: 11px; font-weight: 600; color: #fff; white-space: nowrap; }

  /* ── Module / Sub-Pages ── */
  .wd-mod { background: #faf9ff; min-height: 100vh; }
  .wd-mod-inner { max-width: 1100px; margin: 0 auto; padding: 64px 24px 96px; }
  .wd-back-btn { display: inline-flex; align-items: center; gap: 7px; padding: 7px 16px; border-radius: 999px; border: 1px solid #e2e8f0; background: #fff; color: #475569; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.15s; margin-bottom: 32px; }
  .wd-back-btn:hover { background: #f5f3ff; border-color: #c4b5fd; color: #6d28d9; }
  .wd-mod-kicker { font-size: 12.5px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #6d28d9; margin-bottom: 10px; }
  .wd-mod-title { font-size: clamp(1.5rem, 3vw, 2.4rem); font-weight: 900; color: #0f172a; letter-spacing: -0.03em; margin-bottom: 18px; line-height: 1.12; }
  .wd-mod-desc { font-size: 1.08rem; color: #64748b; line-height: 1.8; max-width: 680px; margin-bottom: 28px; }
  .wd-mod-tags { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 56px; }
  .wd-mod-tag { padding: 5px 14px; border-radius: 999px; background: #f5f3ff; color: #6d28d9; font-size: 12px; font-weight: 600; border: 1px solid #ddd6fe; }
  .wd-mod-section { margin-bottom: 56px; }
  .wd-mod-section h3 { font-size: 1.45rem; font-weight: 800; color: #6d28d9; margin-bottom: 20px; letter-spacing: -0.02em; }
  .wd-mod-split { display: grid; grid-template-columns: minmax(0,1.2fr) minmax(0,1fr); gap: 56px; align-items: start; margin-bottom: 56px; }
  @media (max-width: 900px) { .wd-mod-split { grid-template-columns: 1fr; gap: 36px; } }
  .wd-mod-split-body { font-size: 0.96rem; color: #64748b; line-height: 1.78; margin-bottom: 28px; }
  .wd-mod-check-list { display: flex; flex-direction: column; gap: 12px; }
  .wd-mod-check { display: flex; align-items: flex-start; gap: 10px; font-size: 0.93rem; color: #334155; line-height: 1.6; }
  .wd-mod-check-icon { color: #6d28d9; font-weight: 800; flex-shrink: 0; font-size: 16px; margin-top: 1px; }
  .wd-mod-right-box { background: #fff; border: 1px solid #e2e8f0; border-radius: 20px; padding: 32px; box-shadow: 0 4px 20px rgba(0,0,0,0.04); }
  .wd-mod-right-box h4 { font-size: 1.05rem; font-weight: 700; color: #0f172a; margin-bottom: 18px; }
  .wd-mod-right-row { display: flex; align-items: center; justify-content: space-between; padding: 13px 0; border-bottom: 1px solid #f1f5f9; }
  .wd-mod-right-row:last-child { border-bottom: none; }
  .wd-mod-right-label { font-size: 13px; color: #475569; font-weight: 500; display: flex; align-items: center; gap: 8px; }
  .wd-mod-right-val { font-size: 13px; font-weight: 700; color: #6d28d9; }
  .wd-mod-cards { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 18px; margin-bottom: 48px; }
  @media (max-width: 900px) { .wd-mod-cards { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .wd-mod-cards { grid-template-columns: 1fr; } }
  .wd-mod-card { background: #fff; border-radius: 16px; border: 1px solid #e2e8f0; padding: 24px 22px; box-shadow: 0 1px 3px rgba(15,23,42,0.04); transition: all 0.2s; }
  .wd-mod-card:hover { transform: translateY(-3px); box-shadow: 0 12px 32px rgba(109,40,217,0.1); border-color: #c4b5fd; }
  .wd-mod-card-chip { font-size: 11px; font-weight: 700; color: #6d28d9; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 7px; }
  .wd-mod-card h4 { font-size: 0.98rem; font-weight: 700; color: #0f172a; margin-bottom: 8px; }
  .wd-mod-card p { font-size: 0.87rem; color: #64748b; line-height: 1.65; }
  .wd-mod-scenarios { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 16px; }
  @media (max-width: 900px) { .wd-mod-scenarios { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .wd-mod-scenarios { grid-template-columns: 1fr; } }
  .wd-mod-scenario { background: #1e1b4b; border-radius: 18px; padding: 22px; color: #e5e7eb; box-shadow: 0 10px 28px rgba(15,23,42,0.45); transition: transform 0.18s; }
  .wd-mod-scenario:hover { transform: translateY(-2px) scale(1.01); }
  .wd-mod-scenario-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #a5b4fc; margin-bottom: 6px; }
  .wd-mod-scenario h4 { font-size: 0.97rem; font-weight: 700; margin-bottom: 7px; color: #f1f5f9; }
  .wd-mod-scenario p { font-size: 0.85rem; color: #94a3b8; line-height: 1.62; }
  .wd-mod-faq { border-top: 1px solid #e2e8f0; padding-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 22px; }
  @media (max-width: 900px) { .wd-mod-faq { grid-template-columns: 1fr; } }
  .wd-mod-faq-item h4 { font-size: 0.96rem; font-weight: 700; color: #0f172a; margin-bottom: 6px; }
  .wd-mod-faq-item p { font-size: 0.88rem; color: #64748b; line-height: 1.65; }
  .wd-mod-cta { background: #0f172a; border-radius: 24px; padding: 56px 48px; text-align: center; position: relative; overflow: hidden; margin-top: 64px; }
  @media (max-width: 600px) { .wd-mod-cta { padding: 40px 24px; } }
  .wd-mod-cta-glow { position: absolute; top: -20%; right: -5%; width: 360px; height: 360px; background: radial-gradient(circle, rgba(109,40,217,0.3), transparent 65%); pointer-events: none; }
  .wd-mod-cta h2 { font-size: clamp(1.6rem, 3vw, 2.4rem); font-weight: 900; color: #fff; letter-spacing: -0.03em; margin-bottom: 14px; position: relative; z-index: 1; }
  .wd-mod-cta p { font-size: 1rem; color: #94a3b8; max-width: 480px; margin: 0 auto 32px; line-height: 1.75; position: relative; z-index: 1; }
  .wd-mod-cta-btns { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; }
  .wd-mod-cta-primary { padding: 13px 28px; background: #6d28d9; color: #fff; border: none; border-radius: 11px; font-size: 15px; font-weight: 700; cursor: pointer; transition: background 0.2s; }
  .wd-mod-cta-primary:hover { background: #7c3aed; }
  .wd-mod-cta-secondary { padding: 13px 28px; background: #1e293b; color: #cbd5e1; border: 1px solid #334155; border-radius: 11px; font-size: 15px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
  .wd-mod-cta-secondary:hover { background: #334155; }
`;

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.6 },
};

const stagger = (i: number) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.5, delay: i * 0.1 },
});

const services = [
  { color: 'c-violet',  icon: '🎨', slug: 'custom-design',       title: 'Custom Website Design',           description: 'We design bespoke, pixel-perfect websites built around your brand identity — not templates. Every layout, color palette, and interaction is crafted to convert visitors into customers.', tags: ['UI/UX Design', 'Brand-First', 'Responsive', 'Conversion-Optimized'] },
  { color: 'c-cyan',    icon: '🛒', slug: 'ecommerce-dev',        title: 'E-Commerce Development',          description: 'Full-featured online stores built on Shopify, WooCommerce, or custom stacks — with optimized product pages, seamless checkout flows, and integrations that scale with your business.', tags: ['Shopify', 'WooCommerce', 'Payment Gateways', 'Inventory Sync'] },
  { color: 'c-indigo',  icon: '🚀', slug: 'landing-pages',        title: 'High-Converting Landing Pages',   description: 'Purpose-built landing pages engineered for a single goal — lead capture, product launch, or ad campaigns. Every element is tested to maximize conversion and minimize bounce.', tags: ['Lead Generation', 'A/B Testing', 'CRO', 'Fast Load'] },
  { color: 'c-teal',    icon: '📝', slug: 'cms-wordpress',        title: 'CMS & WordPress Development',     description: 'Powerful content-managed websites that let your team publish, update, and manage content without touching a line of code — built on WordPress or headless CMS platforms.', tags: ['WordPress', 'Headless CMS', 'Content Management', 'Training'] },
  { color: 'c-fuchsia', icon: '🔧', slug: 'maintenance-support',  title: 'Website Maintenance & Support',   description: 'Keep your site secure, fast, and up-to-date with proactive maintenance — plugin updates, security patching, uptime monitoring, and same-day support whenever you need it.', tags: ['Security Patches', 'Uptime Monitoring', 'Speed Optimization', 'Support'] },
  { color: 'c-emerald', icon: '📈', slug: 'seo-performance',      title: 'SEO & Performance Optimization',  description: 'From Core Web Vitals to on-page SEO architecture, we optimize every technical and content layer of your site so Google ranks you higher and visitors stay longer.', tags: ['Core Web Vitals', 'Technical SEO', 'Schema Markup', 'PageSpeed'] },
];

const whyItems = [
  { icon: '⚡', title: 'Sub-3-Second Load Times',      description: 'Every site we build is performance-optimized from the ground up — compressed assets, lazy loading, and CDN delivery ensure your pages load fast on any device.' },
  { icon: '🔒', title: 'Security-First Architecture',   description: 'SSL certificates, HTTPS enforcement, WAF protection, and regular security audits keep your site and your users\' data protected around the clock.' },
  { icon: '📱', title: 'Mobile-First Responsive Design', description: 'With 60%+ of web traffic on mobile, we design for small screens first — ensuring a flawless experience across every device, orientation, and screen size.' },
  { icon: '🎯', title: 'Conversion-Optimized by Default', description: 'Every layout decision — CTA placement, form design, page hierarchy — is grounded in UX research and conversion data, not designer preference.' },
  { icon: '🤝', title: 'Transparent Project Management', description: 'Dedicated project manager, weekly check-ins, shared project dashboard, and zero surprise invoices. You always know exactly where your project stands.' },
  { icon: '📊', title: 'Analytics & Insight Setup',     description: 'GA4, Search Console, heatmaps, and conversion tracking installed at launch — so you can measure ROI from day one and make data-driven improvements.' },
];

const testimonials = [
  { color: '#6d28d9', initials: 'AK', name: 'Alexis Kim',      role: 'Founder, Bloom Boutique',          text: '"Our old website was costing us customers. BizServe redesigned it in three weeks — our mobile conversion rate tripled and our bounce rate dropped by 42%. The ROI was immediate and undeniable."' },
  { color: '#0ea5e9', initials: 'MT', name: 'Marcus Thompson', role: 'CEO, Apex Fitness Co.',             text: '"We launched our Shopify store with BizServe and hit $80k in the first 30 days. The checkout experience they designed is seamless. We\'ve had almost zero cart abandonment complaints since launch."' },
  { color: '#059669', initials: 'PR', name: 'Priya Rajan',     role: 'Director, Summit Legal Group',     text: '"Our WordPress site was slow and impossible to update. BizServe rebuilt it in under four weeks — it now loads in under 2 seconds and our team can update content without calling IT. Game changer."' },
];

const processSteps = [
  { num: '01', chip: 'Discovery',    title: 'Strategy & Scope',        description: 'We deep-dive into your business goals, target audience, and competitive landscape to define the exact website strategy you need.' },
  { num: '02', chip: 'Design',       title: 'UI/UX Wireframes',        description: 'We present wireframes and high-fidelity mockups for your review — refining until every screen is exactly right before a single line of code is written.' },
  { num: '03', chip: 'Development',  title: 'Build & Integrate',       description: 'Our developers bring the approved designs to life with clean, performant code — integrating all third-party tools, CRMs, and payment systems.' },
  { num: '04', chip: 'QA & Testing', title: 'Cross-Device Testing',    description: 'Rigorous testing across devices, browsers, and screen sizes. We don\'t launch until every page passes our 50-point quality checklist.' },
  { num: '05', chip: 'Launch',       title: 'Go Live & Optimize',      description: 'We handle the deployment, configure analytics, and monitor performance post-launch — with ongoing support to keep your site running flawlessly.' },
];

/* ─────────────────────────────────────────
   SERVICE SUB-PAGES
───────────────────────────────────────── */
interface ServicePageProps { onBack: () => void; }

/* ── 1. Custom Website Design ── */
const CustomDesignPage: React.FC<ServicePageProps> = ({ onBack }) => (
  <>
    <section className="wd-svchero">
      <div className="wd-svchero-blob1" />
      <div className="wd-svchero-blob2" />
      <div className="wd-svchero-inner">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <button className="wd-back-btn" onClick={onBack}>← Back to Website Development</button>
          <div className="wd-mod-kicker">Design Service</div>
          <h1 className="wd-mod-title">A Website Built for Your Brand — Not a Template.</h1>
          <p className="wd-hero-sub" style={{ color: '#475569', lineHeight: 1.8, marginBottom: 40, fontSize: '1.1rem' }}>
            Generic templates send a generic message. We design completely custom websites from scratch —
            grounded in your brand strategy, built to convert, and crafted to leave a lasting first impression
            on every visitor.
          </p>
          <div className="wd-mod-tags" style={{ marginBottom: '36px' }}>
            {['Brand Strategy', 'UI/UX Design', 'Wireframing', 'Prototyping', 'Responsive Layouts', 'Conversion Design'].map((t, i) => <span key={i} className="wd-mod-tag">{t}</span>)}
          </div>
          <div className="wd-btn-group">
            <button className="wd-btn-primary">Start your design project →</button>
            <button className="wd-btn-secondary">View our portfolio</button>
          </div>
        </motion.div>
        <motion.div className="wd-svchero-card" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <div className="wd-svchero-card-hdr">
            <div><div className="wd-svchero-hdr-text">Design Project Status</div><div className="wd-svchero-hdr-sub">Live project dashboard</div></div>
            <div className="wd-svchero-hdr-icon">🎨</div>
          </div>
          {[
            { icon: '✅', label: 'Brand Discovery',       val: 'Complete' },
            { icon: '✅', label: 'Wireframes Approved',   val: '12 screens' },
            { icon: '🎨', label: 'High-Fidelity Mockups', val: 'In Review' },
            { icon: '🖥️', label: 'Responsive Variants',   val: 'Desktop + Mobile' },
            { icon: '🔄', label: 'Revision Rounds Used',  val: '1 of 3' },
          ].map((r, i) => (
            <div key={i} className="wd-svchero-row">
              <span className="wd-svchero-row-label">{r.icon} {r.label}</span>
              <span className="wd-svchero-row-val">{r.val}</span>
            </div>
          ))}
          <div className="wd-svchero-metric">
            <div><div className="wd-svchero-metric-label">Project Phase</div><div className="wd-svchero-metric-value">Design Review</div></div>
            <div className="wd-svchero-metric-badge">On track ✓</div>
          </div>
        </motion.div>
      </div>
    </section>

    <section className="wd-mod">
      <div className="wd-mod-inner">
        <div className="wd-mod-split">
          <div>
            <div className="wd-mod-section">
              <h3>Design that converts, not just impresses</h3>
              <p className="wd-mod-split-body">
                Beautiful websites that don't convert are expensive decorations. Our design process starts
                with your business objectives — who your visitors are, what action you want them to take,
                and what your brand needs to communicate — then builds backward to create a site that
                achieves measurable results.
              </p>
              <div className="wd-mod-check-list">
                {[
                  'Full brand strategy session before any design work begins',
                  'Information architecture mapped to visitor intent and conversion goals',
                  'Desktop, tablet, and mobile wireframes reviewed before high-fidelity design',
                  'High-fidelity mockups in Figma with interactive prototype for client review',
                  'Typography, color systems, and component libraries built to scale',
                  'Accessibility standards (WCAG 2.1 AA) met across all designed screens',
                ].map((item, i) => (
                  <div key={i} className="wd-mod-check">
                    <span className="wd-mod-check-icon">✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <div className="wd-mod-right-box">
              <h4>What's Included in Custom Design</h4>
              {[
                { icon: '🗺️', label: 'Sitemap & Architecture',  val: 'Included' },
                { icon: '✏️', label: 'Wireframes',               val: 'All pages' },
                { icon: '🖼️', label: 'Hi-Fi Mockups',           val: 'Desktop + Mobile' },
                { icon: '🔄', label: 'Revision Rounds',          val: 'Up to 3' },
                { icon: '📦', label: 'Design Handoff File',      val: 'Figma source' },
                { icon: '🎨', label: 'Brand Style Guide',        val: 'Included' },
              ].map((r, i) => (
                <div key={i} className="wd-mod-right-row">
                  <span className="wd-mod-right-label">{r.icon} {r.label}</span>
                  <span className="wd-mod-right-val">{r.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Our full design process</h3>
          <div className="wd-mod-cards">
            {[
              { chip: 'Discovery',       title: 'Brand & Audience Research',     body: 'We interview key stakeholders, audit your existing brand assets, and study your top competitors to understand the design landscape before touching a canvas.' },
              { chip: 'Architecture',    title: 'Information Architecture',       body: 'Every page is mapped to a user journey. We define how visitors navigate your site, where they enter, and the exact path that leads them to conversion.' },
              { chip: 'Wireframes',      title: 'Low-Fidelity Wireframes',        body: 'Black-and-white wireframes establish page structure and content hierarchy without the distraction of color or imagery — so layout decisions are made purely on logic.' },
              { chip: 'Visual Design',   title: 'High-Fidelity UI Design',        body: 'Full-color, pixel-perfect mockups with real content, typography, imagery, and interactions — reviewed in an interactive Figma prototype before development begins.' },
              { chip: 'Micro-Interactions', title: 'Hover States & Animations',  body: 'Every button, card, and form element gets thoughtful hover states, transitions, and micro-interactions that make the final site feel polished and premium.' },
              { chip: 'Handoff',         title: 'Developer-Ready Files',          body: 'Design files are organized, annotated, and exported to spec — with a full component library and style guide your dev team can build from with zero ambiguity.' },
            ].map((c, i) => (
              <div key={i} className="wd-mod-card">
                <div className="wd-mod-card-chip">{c.chip}</div>
                <h4>{c.title}</h4>
                <p>{c.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Real-world results</h3>
          <div className="wd-mod-scenarios">
            {[
              { label: 'Rebrand Launch',  title: '3× increase in time-on-site after redesign',       body: 'A consulting firm\'s outdated site had a 72% bounce rate. After a full custom redesign centered on their authority positioning, time-on-site tripled and qualified inbound leads doubled within 60 days.' },
              { label: 'New Business',    title: 'From zero to 40 leads/month in first 90 days',     body: 'A newly launched architecture firm needed a website that matched their premium positioning. Their custom site launched to instant client inquiries — ranking on page 1 for local searches within three months.' },
              { label: 'Trust Signal',    title: 'Franchise closed $200k deal citing website trust', body: 'A franchise operator shared that a $200k B2B contract was influenced by the professionalism of their website. The prospect specifically mentioned the design quality as a trust signal during contract negotiation.' },
            ].map((s, i) => (
              <div key={i} className="wd-mod-scenario">
                <div className="wd-mod-scenario-label">{s.label}</div>
                <h4>{s.title}</h4>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Frequently asked questions</h3>
          <div className="wd-mod-faq">
            {[
              { q: 'How long does the design phase take?',             a: 'For most business websites, the design phase takes 2–4 weeks — including discovery, wireframes, and high-fidelity mockups. Complex or multi-section sites may take 4–6 weeks depending on scope.' },
              { q: 'Can you match our existing brand guidelines?',     a: 'Absolutely. If you have a style guide, logo files, and brand standards, we design within them. If your brand needs an update, we can include a brand refresh as part of the project.' },
              { q: 'How many revision rounds are included?',           a: 'Standard projects include three rounds of revisions on designs. Each revision round covers all feedback submitted in one consolidated review session to keep the project moving forward.' },
              { q: 'Do we own the final design files?',                a: 'Yes. Upon final payment, all design files — including Figma source files, exported assets, and the style guide — are transferred to you. You own everything we create for your project.' },
            ].map((f, i) => (
              <div key={i} className="wd-mod-faq-item">
                <h4>{f.q}</h4>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-cta">
          <div className="wd-mod-cta-glow" />
          <h2>Your brand deserves a website that matches its quality.</h2>
          <p>Start with a free strategy session — we'll scope your project and share design examples before any commitment.</p>
          <div className="wd-mod-cta-btns">
            <button className="wd-mod-cta-primary">Start my design project →</button>
            <button className="wd-mod-cta-secondary">Schedule a free consultation</button>
          </div>
        </div>
      </div>
    </section>
  </>
);

/* ── 2. E-Commerce Development ── */
const EcommercePage: React.FC<ServicePageProps> = ({ onBack }) => (
  <>
    <section className="wd-svchero">
      <div className="wd-svchero-blob1" />
      <div className="wd-svchero-blob2" />
      <div className="wd-svchero-inner">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <button className="wd-back-btn" onClick={onBack}>← Back to Website Development</button>
          <div className="wd-mod-kicker">E-Commerce</div>
          <h1 className="wd-mod-title">An Online Store Built to Sell — Not Just Look Good.</h1>
          <p className="wd-hero-sub" style={{ color: '#475569', lineHeight: 1.8, marginBottom: 40, fontSize: '1.1rem' }}>
            We build e-commerce experiences that reduce friction at every step of the buyer journey —
            from first product impression to completed checkout — with the integrations, speed, and
            scalability your growing store demands.
          </p>
          <div className="wd-mod-tags" style={{ marginBottom: '36px' }}>
            {['Shopify', 'WooCommerce', 'Product Pages', 'Checkout Optimization', 'Payment Gateways', 'Inventory Management'].map((t, i) => <span key={i} className="wd-mod-tag">{t}</span>)}
          </div>
          <div className="wd-btn-group">
            <button className="wd-btn-primary">Build my online store →</button>
            <button className="wd-btn-secondary">See e-commerce examples</button>
          </div>
        </motion.div>
        <motion.div className="wd-svchero-card" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <div className="wd-svchero-card-hdr">
            <div><div className="wd-svchero-hdr-text">Store Performance — Live</div><div className="wd-svchero-hdr-sub">Last 30 days</div></div>
            <div className="wd-svchero-hdr-icon">🛒</div>
          </div>
          {[
            { icon: '💰', label: 'Monthly Revenue',        val: '$84,230' },
            { icon: '🛒', label: 'Orders Processed',       val: '1,247' },
            { icon: '📈', label: 'Conversion Rate',        val: '4.2%' },
            { icon: '⚡', label: 'Avg. Page Load',         val: '1.4s' },
            { icon: '🔄', label: 'Cart Abandon Rate',      val: '12% (low)' },
          ].map((r, i) => (
            <div key={i} className="wd-svchero-row">
              <span className="wd-svchero-row-label">{r.icon} {r.label}</span>
              <span className="wd-svchero-row-val">{r.val}</span>
            </div>
          ))}
          <div className="wd-svchero-metric">
            <div><div className="wd-svchero-metric-label">Revenue Growth</div><div className="wd-svchero-metric-value">+38% MoM</div></div>
            <div className="wd-svchero-metric-badge">↑ vs last month</div>
          </div>
        </motion.div>
      </div>
    </section>

    <section className="wd-mod">
      <div className="wd-mod-inner">
        <div className="wd-mod-split">
          <div>
            <div className="wd-mod-section">
              <h3>E-commerce that converts browsers into buyers</h3>
              <p className="wd-mod-split-body">
                Most e-commerce stores lose 70% of their potential customers to a poor checkout experience,
                slow load times, or confusing product pages. We engineer every detail of your store to
                minimize friction and maximize the percentage of visitors who complete a purchase.
              </p>
              <div className="wd-mod-check-list">
                {[
                  'Shopify, WooCommerce, or custom headless e-commerce builds',
                  'Product page optimization with high-converting layouts and imagery guidelines',
                  'One-page checkout flows designed to reduce abandonment by up to 60%',
                  'Payment gateway integrations — Stripe, PayPal, Shop Pay, Apple Pay, and more',
                  'Inventory management, SKU organization, and variant logic configured to your catalog',
                  'Post-launch analytics dashboards tracking revenue, conversion, and AOV',
                ].map((item, i) => (
                  <div key={i} className="wd-mod-check">
                    <span className="wd-mod-check-icon">✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <div className="wd-mod-right-box">
              <h4>E-Commerce Build Scope</h4>
              {[
                { icon: '🏪', label: 'Platform',               val: 'Shopify / WooCommerce' },
                { icon: '📦', label: 'Product Setup',          val: 'Up to 500 SKUs' },
                { icon: '💳', label: 'Payment Gateways',       val: '5+ integrations' },
                { icon: '📧', label: 'Email Flows',            val: 'Abandon + Post-Purchase' },
                { icon: '📊', label: 'Analytics',              val: 'GA4 + Meta Pixel' },
                { icon: '🔒', label: 'Security & SSL',         val: 'Fully configured' },
              ].map((r, i) => (
                <div key={i} className="wd-mod-right-row">
                  <span className="wd-mod-right-label">{r.icon} {r.label}</span>
                  <span className="wd-mod-right-val">{r.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Every layer of your store, built to perform</h3>
          <div className="wd-mod-cards">
            {[
              { chip: 'Product Pages',    title: 'High-Converting Product Layouts', body: 'We design product pages proven to convert — with social proof placement, urgency triggers, clear variant selection, and trust badges strategically positioned.' },
              { chip: 'Checkout',         title: 'Frictionless Checkout Flow',       body: 'Our checkout builds strip away every unnecessary step and form field. Guest checkout, address autocomplete, and one-click upsells are standard on every store.' },
              { chip: 'Performance',      title: 'Sub-2-Second Store Speed',         body: 'Image optimization, lazy loading, and CDN delivery keep every product page loading fast — critical for both SEO rankings and preventing mobile abandonment.' },
              { chip: 'Integrations',     title: 'CRM & Marketing Integrations',     body: 'We connect your store to Klaviyo, Mailchimp, HubSpot, or your CRM of choice — so every order and abandoned cart automatically triggers the right follow-up.' },
              { chip: 'Mobile UX',        title: 'Mobile Commerce First',            body: 'With 70%+ of e-commerce traffic on mobile, we design the mobile buying experience first — with thumb-friendly navigation, swipeable galleries, and fast tap targets.' },
              { chip: 'Post-Launch',      title: 'Ongoing Optimization',             body: 'We monitor conversion rates, run A/B tests on key pages, and make data-driven improvements quarterly to continuously increase your store\'s revenue per visitor.' },
            ].map((c, i) => (
              <div key={i} className="wd-mod-card">
                <div className="wd-mod-card-chip">{c.chip}</div>
                <h4>{c.title}</h4>
                <p>{c.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Real-world results</h3>
          <div className="wd-mod-scenarios">
            {[
              { label: 'Launch',          title: '$80k revenue in the first 30 days post-launch', body: 'A fitness supplement brand launched on Shopify with a BizServe-built store. Optimized product pages and a seamless checkout drove $80,000 in sales in the first month — with a 4.8% conversion rate.' },
              { label: 'Migration',       title: 'Migrated from Wix, revenue increased 2.4×',    body: 'A home goods retailer moved from a DIY Wix store to a custom WooCommerce build. Better product organization, faster load times, and a redesigned checkout doubled their conversion rate within 45 days.' },
              { label: 'Cart Recovery',   title: 'Abandoned cart flow recovered $14k/month',      body: 'After implementing a 3-step abandoned cart email sequence and simplifying the checkout to one page, a clothing brand recovered an average of $14,000 per month in revenue that previously went uncaptured.' },
            ].map((s, i) => (
              <div key={i} className="wd-mod-scenario">
                <div className="wd-mod-scenario-label">{s.label}</div>
                <h4>{s.title}</h4>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Frequently asked questions</h3>
          <div className="wd-mod-faq">
            {[
              { q: 'Shopify or WooCommerce — which is right for me?',     a: 'Shopify is ideal for businesses that want a hosted, low-maintenance platform with fast setup. WooCommerce suits businesses needing deep customization or complex integrations. We help you choose based on your catalog size, budget, and technical preferences.' },
              { q: 'Can you migrate my existing store?',                   a: 'Yes. We migrate products, customer data, order history, and URLs from virtually any platform — including BigCommerce, Wix, Squarespace, Magento, and custom builds — with zero downtime during transition.' },
              { q: 'Do you set up payment processing?',                    a: 'We handle full payment gateway configuration — Stripe, PayPal, Shop Pay, Apple Pay, Google Pay, and buy-now-pay-later options. We also configure tax settings, shipping zones, and currency handling.' },
              { q: 'What happens after the store launches?',               a: 'Every store launch includes 30 days of post-launch support at no additional cost. After that, we offer ongoing maintenance plans or can hand off the store to your team with full documentation and training.' },
            ].map((f, i) => (
              <div key={i} className="wd-mod-faq-item">
                <h4>{f.q}</h4>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-cta">
          <div className="wd-mod-cta-glow" />
          <h2>Your next customer is one click away.</h2>
          <p>Get a free e-commerce strategy session — we'll audit your current setup and show you exactly what's costing you conversions.</p>
          <div className="wd-mod-cta-btns">
            <button className="wd-mod-cta-primary">Build my online store →</button>
            <button className="wd-mod-cta-secondary">Schedule a free consultation</button>
          </div>
        </div>
      </div>
    </section>
  </>
);

/* ── 3. Landing Pages ── */
const LandingPagesPage: React.FC<ServicePageProps> = ({ onBack }) => (
  <>
    <section className="wd-svchero">
      <div className="wd-svchero-blob1" />
      <div className="wd-svchero-blob2" />
      <div className="wd-svchero-inner">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <button className="wd-back-btn" onClick={onBack}>← Back to Website Development</button>
          <div className="wd-mod-kicker">Conversion Optimization</div>
          <h1 className="wd-mod-title">Landing Pages Engineered to Convert at Scale.</h1>
          <p className="wd-hero-sub" style={{ color: '#475569', lineHeight: 1.8, marginBottom: 40, fontSize: '1.1rem' }}>
            Every ad dollar you spend ends on a landing page. We build pages where nothing distracts from
            the single goal — whether that's capturing a lead, booking a demo, or closing a sale.
            Built fast, A/B tested, and optimized relentlessly.
          </p>
          <div className="wd-mod-tags" style={{ marginBottom: '36px' }}>
            {['Lead Capture', 'Sales Pages', 'Webinar Registration', 'Product Launch', 'A/B Testing', 'CRO'].map((t, i) => <span key={i} className="wd-mod-tag">{t}</span>)}
          </div>
          <div className="wd-btn-group">
            <button className="wd-btn-primary">Build my landing page →</button>
            <button className="wd-btn-secondary">See conversion examples</button>
          </div>
        </motion.div>
        <motion.div className="wd-svchero-card" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <div className="wd-svchero-card-hdr">
            <div><div className="wd-svchero-hdr-text">Landing Page Performance</div><div className="wd-svchero-hdr-sub">Google Ads Campaign — Live</div></div>
            <div className="wd-svchero-hdr-icon">🚀</div>
          </div>
          {[
            { icon: '👁️', label: 'Monthly Visitors',       val: '12,840' },
            { icon: '✅', label: 'Conversion Rate',         val: '11.3%' },
            { icon: '📋', label: 'Leads Generated',         val: '1,451' },
            { icon: '💲', label: 'Cost Per Lead',           val: '$8.40' },
            { icon: '⚡', label: 'Page Load Speed',         val: '0.9s' },
          ].map((r, i) => (
            <div key={i} className="wd-svchero-row">
              <span className="wd-svchero-row-label">{r.icon} {r.label}</span>
              <span className="wd-svchero-row-val">{r.val}</span>
            </div>
          ))}
          <div className="wd-svchero-metric">
            <div><div className="wd-svchero-metric-label">vs. Old Page</div><div className="wd-svchero-metric-value">+4.1× CVR</div></div>
            <div className="wd-svchero-metric-badge">A/B test winner ✓</div>
          </div>
        </motion.div>
      </div>
    </section>

    <section className="wd-mod">
      <div className="wd-mod-inner">
        <div className="wd-mod-split">
          <div>
            <div className="wd-mod-section">
              <h3>One page, one goal, maximum results</h3>
              <p className="wd-mod-split-body">
                The average landing page converts at 2–3%. Our pages consistently deliver 8–12% conversion
                rates because every element — headline, hero image, CTA placement, form length, social proof —
                is a deliberate decision backed by conversion science and tested against real traffic.
              </p>
              <div className="wd-mod-check-list">
                {[
                  'Conversion-focused copywriting with proven headline formulas included',
                  'Above-the-fold optimization ensuring the value proposition lands in 3 seconds',
                  'Social proof architecture — testimonials, logos, case studies, and review counts placed strategically',
                  'Form optimization reducing fields to the minimum required to qualify a lead',
                  'A/B testing variants built simultaneously so you can start testing on day one',
                  'Full CRM and ad platform integration — HubSpot, Salesforce, Meta, Google Ads',
                ].map((item, i) => (
                  <div key={i} className="wd-mod-check">
                    <span className="wd-mod-check-icon">✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <div className="wd-mod-right-box">
              <h4>Landing Page Deliverables</h4>
              {[
                { icon: '🎯', label: 'Strategy Session',       val: 'Included' },
                { icon: '✍️', label: 'Conversion Copywriting', val: 'Included' },
                { icon: '🖼️', label: 'Custom Design',          val: 'Desktop + Mobile' },
                { icon: '🔗', label: 'CRM Integration',        val: 'Included' },
                { icon: '🧪', label: 'A/B Test Variants',      val: '2 variants' },
                { icon: '📊', label: 'Analytics Setup',        val: 'GA4 + Pixel' },
              ].map((r, i) => (
                <div key={i} className="wd-mod-right-row">
                  <span className="wd-mod-right-label">{r.icon} {r.label}</span>
                  <span className="wd-mod-right-val">{r.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Every landing page type, built to win</h3>
          <div className="wd-mod-cards">
            {[
              { chip: 'Lead Gen',      title: 'Lead Capture Pages',          body: 'Capture qualified leads from paid search, social, or organic traffic with forms engineered to maximize submissions while pre-qualifying contacts for your sales team.' },
              { chip: 'Sales',         title: 'Long-Form Sales Pages',        body: 'For high-ticket offers, we build persuasive long-form pages that address every objection, demonstrate value, and move cold visitors to a confident purchase decision.' },
              { chip: 'Launch',        title: 'Product Launch Pages',         body: 'Build anticipation and capture early buyers with countdown timers, waitlist flows, and launch-day messaging that creates urgency without feeling cheap.' },
              { chip: 'Webinar',       title: 'Webinar & Event Registration', body: 'Maximize registrations with speaker credibility, agenda previews, and multi-step registration flows optimized for the specific psychology of live event signups.' },
              { chip: 'PPC',           title: 'Ad-Specific Landing Pages',    body: 'Message-match each ad group to a dedicated landing page — dramatically improving Quality Scores, reducing cost-per-click, and increasing conversion rates.' },
              { chip: 'SaaS',          title: 'Free Trial & Demo Request',    body: 'For SaaS products, we build trial sign-up and demo request pages that communicate value fast, reduce signup friction, and qualify leads before they enter the pipeline.' },
            ].map((c, i) => (
              <div key={i} className="wd-mod-card">
                <div className="wd-mod-card-chip">{c.chip}</div>
                <h4>{c.title}</h4>
                <p>{c.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Real-world results</h3>
          <div className="wd-mod-scenarios">
            {[
              { label: 'PPC Campaign',    title: 'Cut cost per lead from $42 to $9.80',       body: 'A home services company was sending all Google Ads traffic to their homepage. A dedicated landing page with message-matched copy and a simplified form reduced their CPL by 77% in the first 30 days.' },
              { label: 'SaaS Launch',     title: '3,200 trial signups in 7-day launch window', body: 'A productivity SaaS used a BizServe-built launch page with a 5-day email countdown sequence and limited-time pricing. 3,200 trials started in the first week, 14% converted to paid within 30 days.' },
              { label: 'Real Estate',     title: '18% conversion rate on luxury listings',     body: 'A luxury real estate developer running Facebook Ads to their main site saw 1.8% lead conversion. A dedicated listing page with virtual tour integration, social proof, and a one-field form hit 18%.' },
            ].map((s, i) => (
              <div key={i} className="wd-mod-scenario">
                <div className="wd-mod-scenario-label">{s.label}</div>
                <h4>{s.title}</h4>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Frequently asked questions</h3>
          <div className="wd-mod-faq">
            {[
              { q: 'How quickly can a landing page go live?',        a: 'Standard landing pages go from brief to live in 5–10 business days. Rush builds for time-sensitive campaigns can be delivered in as few as 3 business days depending on complexity.' },
              { q: 'Do you write the copy or do we provide it?',     a: 'We write conversion-optimized copy for all of our landing pages as part of the build. You review and approve before launch. If you have existing copy you\'d like us to work with, we can optimize and format it.' },
              { q: 'Can you build pages that match our existing site?', a: 'Yes. We match fonts, colors, brand assets, and visual style to ensure the landing page feels seamless whether visitors arrive from an ad or navigate from your website.' },
              { q: 'What platforms do you build on?',                 a: 'We build on Webflow, WordPress/Elementor, Unbounce, HubSpot, or directly in HTML/CSS — depending on your CRM, ad platform, and hosting preferences. We recommend Webflow for speed and flexibility.' },
            ].map((f, i) => (
              <div key={i} className="wd-mod-faq-item">
                <h4>{f.q}</h4>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-cta">
          <div className="wd-mod-cta-glow" />
          <h2>Stop wasting ad spend on low-converting pages.</h2>
          <p>Get a free conversion audit of your current landing page — we'll show you exactly what to fix and what it could mean for your CPL.</p>
          <div className="wd-mod-cta-btns">
            <button className="wd-mod-cta-primary">Build my landing page →</button>
            <button className="wd-mod-cta-secondary">Get a free audit</button>
          </div>
        </div>
      </div>
    </section>
  </>
);

/* ── 4. CMS & WordPress ── */
const CMSWordPressPage: React.FC<ServicePageProps> = ({ onBack }) => (
  <>
    <section className="wd-svchero">
      <div className="wd-svchero-blob1" />
      <div className="wd-svchero-blob2" />
      <div className="wd-svchero-inner">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <button className="wd-back-btn" onClick={onBack}>← Back to Website Development</button>
          <div className="wd-mod-kicker">Content Management</div>
          <h1 className="wd-mod-title">Your Team Controls the Content. We Build the Foundation.</h1>
          <p className="wd-hero-sub" style={{ color: '#475569', lineHeight: 1.8, marginBottom: 40, fontSize: '1.1rem' }}>
            A great CMS means your team can update, publish, and manage your website without waiting on a
            developer. We build WordPress and headless CMS platforms that are powerful under the hood and
            effortless to use every day.
          </p>
          <div className="wd-mod-tags" style={{ marginBottom: '36px' }}>
            {['WordPress', 'Sanity', 'Contentful', 'Webflow CMS', 'Blog Setup', 'Team Training'].map((t, i) => <span key={i} className="wd-mod-tag">{t}</span>)}
          </div>
          <div className="wd-btn-group">
            <button className="wd-btn-primary">Build my CMS website →</button>
            <button className="wd-btn-secondary">Talk to a specialist</button>
          </div>
        </motion.div>
        <motion.div className="wd-svchero-card" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <div className="wd-svchero-card-hdr">
            <div><div className="wd-svchero-hdr-text">CMS Dashboard — Overview</div><div className="wd-svchero-hdr-sub">WordPress + Custom Blocks</div></div>
            <div className="wd-svchero-hdr-icon">📝</div>
          </div>
          {[
            { icon: '📄', label: 'Pages Published',       val: '48 pages' },
            { icon: '📰', label: 'Blog Posts',            val: '124 articles' },
            { icon: '👥', label: 'Team Editors',          val: '6 users' },
            { icon: '🔌', label: 'Plugins Active',        val: '12 (audited)' },
            { icon: '🔒', label: 'Security Status',       val: 'All clear ✓' },
          ].map((r, i) => (
            <div key={i} className="wd-svchero-row">
              <span className="wd-svchero-row-label">{r.icon} {r.label}</span>
              <span className="wd-svchero-row-val">{r.val}</span>
            </div>
          ))}
          <div className="wd-svchero-metric">
            <div><div className="wd-svchero-metric-label">Last Content Update</div><div className="wd-svchero-metric-value">Today, 10:14 AM</div></div>
            <div className="wd-svchero-metric-badge">No dev needed ✓</div>
          </div>
        </motion.div>
      </div>
    </section>

    <section className="wd-mod">
      <div className="wd-mod-inner">
        <div className="wd-mod-split">
          <div>
            <div className="wd-mod-section">
              <h3>A website your team can actually manage</h3>
              <p className="wd-mod-split-body">
                The most expensive website is one that requires a developer for every content update.
                We build CMS-powered sites with intuitive editing interfaces so your marketing team can
                publish blog posts, update team pages, swap images, and manage content — completely
                independently, with no risk of breaking anything.
              </p>
              <div className="wd-mod-check-list">
                {[
                  'Custom Gutenberg blocks or page builder setup tailored to your content types',
                  'Role-based permissions — editors can publish, reviewers can draft, admins control all',
                  'Custom post types for team members, case studies, services, portfolios, and more',
                  'Multi-author blog infrastructure with categories, tags, and SEO fields built in',
                  'Live 2-hour editor training session for your entire team included at launch',
                  'Written documentation and video walkthroughs provided for ongoing reference',
                ].map((item, i) => (
                  <div key={i} className="wd-mod-check">
                    <span className="wd-mod-check-icon">✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <div className="wd-mod-right-box">
              <h4>CMS Build Includes</h4>
              {[
                { icon: '🏗️', label: 'Custom Theme Build',       val: 'From scratch' },
                { icon: '🧱', label: 'Page Builder Blocks',       val: 'Unlimited custom' },
                { icon: '🔌', label: 'Plugin Audit & Setup',      val: 'Security + SEO' },
                { icon: '🚀', label: 'Performance Config',        val: 'Caching + CDN' },
                { icon: '🎓', label: 'Team Training',              val: '2-hour session' },
                { icon: '📚', label: 'Documentation',              val: 'Video + written' },
              ].map((r, i) => (
                <div key={i} className="wd-mod-right-row">
                  <span className="wd-mod-right-label">{r.icon} {r.label}</span>
                  <span className="wd-mod-right-val">{r.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>CMS platforms we build on</h3>
          <div className="wd-mod-cards">
            {[
              { chip: 'WordPress',    title: 'WordPress Custom Themes',      body: 'Custom-coded WordPress themes — no page builder bloat, no theme-shop limitations. Clean, fast, and built to your exact specifications with a Gutenberg editing experience.' },
              { chip: 'Webflow',      title: 'Webflow CMS Builds',           body: 'Webflow\'s visual CMS is ideal for design-heavy sites that need marketing teams to update content without engineering involvement. Fast, secure, and zero hosting headaches.' },
              { chip: 'Sanity',       title: 'Sanity (Headless CMS)',        body: 'For businesses with complex content models or multiple front-end channels, Sanity delivers a structured, API-first CMS that scales across web, mobile, and digital signage.' },
              { chip: 'Contentful',   title: 'Contentful Enterprise CMS',    body: 'Enterprise-grade content management with localization, workflows, and approval chains built in. Ideal for multi-region businesses with distributed editorial teams.' },
              { chip: 'Multisite',    title: 'WordPress Multisite Networks', body: 'Manage dozens of websites from a single WordPress installation — with shared plugins, centralized user management, and the ability to push template updates across the entire network.' },
              { chip: 'Migration',    title: 'CMS Migration & Rebuilds',     body: 'Moving from Wix, Squarespace, Drupal, or a legacy custom CMS? We migrate all content, preserve URLs and SEO rankings, and rebuild your editing experience in a modern platform.' },
            ].map((c, i) => (
              <div key={i} className="wd-mod-card">
                <div className="wd-mod-card-chip">{c.chip}</div>
                <h4>{c.title}</h4>
                <p>{c.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Real-world results</h3>
          <div className="wd-mod-scenarios">
            {[
              { label: 'In-House Control',  title: 'Marketing team now publishes 3× more content',    body: 'A professional services firm\'s marketing team was bottlenecked waiting on their developer for every page update. After a Webflow CMS rebuild, they publish new content 3× faster — entirely independently.' },
              { label: 'Speed Gain',        title: 'Page load dropped from 8.2s to 1.6s after rebuild', body: 'A nonprofit\'s WordPress site had accumulated 47 plugins over five years, causing 8-second load times. A clean-slate rebuild with 12 audited plugins and a custom theme brought it to 1.6 seconds.' },
              { label: 'Content Scale',     title: 'Blog drove 14,000 organic sessions in 6 months',  body: 'A B2B software company launched their blog on a BizServe-built WordPress site with full SEO infrastructure. In 6 months, 14,000 organic monthly sessions were attributed directly to blog content.' },
            ].map((s, i) => (
              <div key={i} className="wd-mod-scenario">
                <div className="wd-mod-scenario-label">{s.label}</div>
                <h4>{s.title}</h4>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Frequently asked questions</h3>
          <div className="wd-mod-faq">
            {[
              { q: 'Do I need technical knowledge to manage a WordPress site?', a: 'Not after we set it up. We configure a custom block-based editing experience so your team can update pages, add blog posts, and manage content using a simple drag-and-drop interface — no coding required.' },
              { q: 'Is WordPress secure?',                                       a: 'WordPress core is extremely secure when properly configured. We harden every installation — removing default usernames, adding two-factor authentication, installing a WAF, and setting up automated offsite backups.' },
              { q: 'Will my site be slow because it\'s on WordPress?',           a: 'Not with proper setup. We implement server-side caching, CDN delivery, optimized image loading, and a minimal plugin footprint. Our WordPress builds consistently score 90+ on Google PageSpeed.' },
              { q: 'Can you migrate our existing WordPress site?',               a: 'Yes. We migrate existing WordPress sites to new hosting, rebuild aging themes into clean custom code, and can restructure the content architecture while preserving all existing content and SEO value.' },
            ].map((f, i) => (
              <div key={i} className="wd-mod-faq-item">
                <h4>{f.q}</h4>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-cta">
          <div className="wd-mod-cta-glow" />
          <h2>Give your team the content control they need.</h2>
          <p>Stop waiting on developers for every update. We'll build a CMS your entire team can confidently use from day one.</p>
          <div className="wd-mod-cta-btns">
            <button className="wd-mod-cta-primary">Build my CMS website →</button>
            <button className="wd-mod-cta-secondary">Schedule a free consultation</button>
          </div>
        </div>
      </div>
    </section>
  </>
);

/* ── 5. Maintenance & Support ── */
const MaintenancePage: React.FC<ServicePageProps> = ({ onBack }) => (
  <>
    <section className="wd-svchero">
      <div className="wd-svchero-blob1" />
      <div className="wd-svchero-blob2" />
      <div className="wd-svchero-inner">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <button className="wd-back-btn" onClick={onBack}>← Back to Website Development</button>
          <div className="wd-mod-kicker">Ongoing Support</div>
          <h1 className="wd-mod-title">Your Website, Monitored and Protected 24/7.</h1>
          <p className="wd-hero-sub" style={{ color: '#475569', lineHeight: 1.8, marginBottom: 40, fontSize: '1.1rem' }}>
            A website isn't a one-time project — it's a living asset that needs security patches, speed
            monitoring, plugin updates, and proactive care. We handle all of it so your site stays fast,
            secure, and earning for your business every single day.
          </p>
          <div className="wd-mod-tags" style={{ marginBottom: '36px' }}>
            {['Uptime Monitoring', 'Security Patches', 'Plugin Updates', 'Daily Backups', 'Speed Optimization', 'Priority Support'].map((t, i) => <span key={i} className="wd-mod-tag">{t}</span>)}
          </div>
          <div className="wd-btn-group">
            <button className="wd-btn-primary">Protect my website →</button>
            <button className="wd-btn-secondary">View maintenance plans</button>
          </div>
        </motion.div>
        <motion.div className="wd-svchero-card" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <div className="wd-svchero-card-hdr">
            <div><div className="wd-svchero-hdr-text">Site Health Monitor</div><div className="wd-svchero-hdr-sub">Real-time protection status</div></div>
            <div className="wd-svchero-hdr-icon">🔧</div>
          </div>
          {[
            { icon: '✅', label: 'Uptime Status',           val: '99.98% / 30 days' },
            { icon: '🛡️', label: 'Security Scan',           val: 'No threats found' },
            { icon: '🔄', label: 'Core / Plugin Updates',   val: 'All current' },
            { icon: '💾', label: 'Last Backup',             val: 'Today 3:00 AM' },
            { icon: '⚡', label: 'PageSpeed Score',         val: '96 / 100' },
          ].map((r, i) => (
            <div key={i} className="wd-svchero-row">
              <span className="wd-svchero-row-label">{r.icon} {r.label}</span>
              <span className="wd-svchero-row-val">{r.val}</span>
            </div>
          ))}
          <div className="wd-svchero-metric">
            <div><div className="wd-svchero-metric-label">Overall Site Health</div><div className="wd-svchero-metric-value">Excellent</div></div>
            <div className="wd-svchero-metric-badge">All systems green ✓</div>
          </div>
        </motion.div>
      </div>
    </section>

    <section className="wd-mod">
      <div className="wd-mod-inner">
        <div className="wd-mod-split">
          <div>
            <div className="wd-mod-section">
              <h3>Proactive care beats reactive panic</h3>
              <p className="wd-mod-split-body">
                The average hacked website costs $4,700 to recover and is offline for 2–3 days. Outdated
                plugins and themes are the #1 attack vector. Our maintenance plans catch vulnerabilities
                before they become crises — with daily security scans, proactive update management, and
                a team ready to respond same-day when anything needs attention.
              </p>
              <div className="wd-mod-check-list">
                {[
                  'Daily automated backups stored offsite with 30-day retention and one-click restore',
                  'Weekly core, plugin, and theme updates tested on a staging environment before deployment',
                  '24/7 uptime monitoring with instant alerts and escalation if downtime is detected',
                  'Daily malware and vulnerability scanning with automatic quarantine of detected threats',
                  'Monthly performance audits — PageSpeed, Core Web Vitals, and load time benchmarking',
                  'Dedicated support ticket system with 4-business-hour response SLA on all plans',
                ].map((item, i) => (
                  <div key={i} className="wd-mod-check">
                    <span className="wd-mod-check-icon">✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <div className="wd-mod-right-box">
              <h4>Monthly Maintenance Report</h4>
              {[
                { icon: '🔄', label: 'Updates Applied',        val: '14 updates' },
                { icon: '🛡️', label: 'Threats Blocked',        val: '38 attempts' },
                { icon: '💾', label: 'Backups Completed',      val: '31 of 31' },
                { icon: '⚡', label: 'Avg Load Time',          val: '1.3 seconds' },
                { icon: '📊', label: 'Uptime',                 val: '99.98%' },
                { icon: '🎫', label: 'Support Tickets',        val: '2 resolved' },
              ].map((r, i) => (
                <div key={i} className="wd-mod-right-row">
                  <span className="wd-mod-right-label">{r.icon} {r.label}</span>
                  <span className="wd-mod-right-val">{r.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Everything covered, nothing missed</h3>
          <div className="wd-mod-cards">
            {[
              { chip: 'Security',        title: 'Proactive Security Hardening',  body: 'WAF configuration, brute force protection, file integrity monitoring, and login lockouts — layered defenses that stop threats before they reach your site.' },
              { chip: 'Updates',         title: 'Managed Plugin & Core Updates', body: 'We test every update on a staging clone before pushing live — ensuring nothing breaks. Compatibility conflicts are resolved before they ever affect your visitors.' },
              { chip: 'Backups',         title: 'Daily Automated Backups',       body: 'Full-site backups every day, stored offsite in redundant cloud storage. If anything ever goes wrong, we can restore your site to any point in the last 30 days within minutes.' },
              { chip: 'Performance',     title: 'Speed Monitoring & Tuning',     body: 'Monthly PageSpeed audits with active optimization — image compression, cache tuning, database cleanup, and CDN management keep your scores high as your content grows.' },
              { chip: 'Uptime',          title: '24/7 Uptime Monitoring',        body: 'Checks every 60 seconds from multiple global locations. If your site goes down, we get the alert before you do — and our team is already on it.' },
              { chip: 'Support',         title: 'Priority Support Access',       body: 'Dedicated support portal with guaranteed 4-business-hour response for all maintenance plans. Emergency response available for critical issues on Pro and above plans.' },
            ].map((c, i) => (
              <div key={i} className="wd-mod-card">
                <div className="wd-mod-card-chip">{c.chip}</div>
                <h4>{c.title}</h4>
                <p>{c.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Real-world results</h3>
          <div className="wd-mod-scenarios">
            {[
              { label: 'Hack Recovery',   title: 'Caught & contained in 4 minutes flat',          body: 'A BizServe maintenance client\'s site was targeted by a credential stuffing attack at 2 AM. Our monitoring detected anomalous login activity, automatically blocked the IP range, and alerted our on-call team — all before a single unauthorized login succeeded.' },
              { label: 'Prevented Loss',  title: 'Plugin update conflict caught on staging',       body: 'A WooCommerce update conflicted with a custom checkout plugin and would have broken the store\'s payment flow completely. Our staged update process caught it before deployment. The client never knew it happened.' },
              { label: 'Speed Impact',    title: 'Performance tune improved conversions by 18%',   body: 'Monthly performance auditing found a bloated homepage image and render-blocking script. After optimization, LCP improved from 4.1s to 1.7s — and the client saw an 18% lift in contact form submissions.' },
            ].map((s, i) => (
              <div key={i} className="wd-mod-scenario">
                <div className="wd-mod-scenario-label">{s.label}</div>
                <h4>{s.title}</h4>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Frequently asked questions</h3>
          <div className="wd-mod-faq">
            {[
              { q: 'What if my site gets hacked while on a maintenance plan?', a: 'If a security incident occurs on a site we manage, we handle full remediation at no additional cost — including malware removal, file restoration, blacklist removal, and a post-incident security audit.' },
              { q: 'Do you work on websites you didn\'t build?',               a: 'Yes. We onboard existing websites onto maintenance plans after an initial audit. If we identify security or performance issues during onboarding, we\'ll provide a clear remediation plan before beginning recurring maintenance.' },
              { q: 'How do updates get handled without breaking things?',      a: 'All updates are applied to a staging clone first and tested against your live functionality. Only after confirming nothing is broken are updates deployed to production — with a backup taken immediately before the push.' },
              { q: 'What\'s included in the monthly performance report?',      a: 'Each month you receive a report covering: updates applied, security events, backup status, uptime percentage, PageSpeed scores, Core Web Vitals, and any recommendations for the next month.' },
            ].map((f, i) => (
              <div key={i} className="wd-mod-faq-item">
                <h4>{f.q}</h4>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-cta">
          <div className="wd-mod-cta-glow" />
          <h2>Don't wait for a hack to take website security seriously.</h2>
          <p>Get your first month of managed maintenance free — we'll audit your site and show you exactly what needs protecting.</p>
          <div className="wd-mod-cta-btns">
            <button className="wd-mod-cta-primary">Protect my website →</button>
            <button className="wd-mod-cta-secondary">Get a free site audit</button>
          </div>
        </div>
      </div>
    </section>
  </>
);

/* ── 6. SEO & Performance ── */
const SEOPerformancePage: React.FC<ServicePageProps> = ({ onBack }) => (
  <>
    <section className="wd-svchero">
      <div className="wd-svchero-blob1" />
      <div className="wd-svchero-blob2" />
      <div className="wd-svchero-inner">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <button className="wd-back-btn" onClick={onBack}>← Back to Website Development</button>
          <div className="wd-mod-kicker">SEO & Performance</div>
          <h1 className="wd-mod-title">Get Found on Google. Get Visited. Get Customers.</h1>
          <p className="wd-hero-sub" style={{ color: '#475569', lineHeight: 1.8, marginBottom: 40, fontSize: '1.1rem' }}>
            A beautiful website that nobody finds is a liability, not an asset. We optimize every
            technical and content layer of your site — from Core Web Vitals to structured data — so
            Google ranks you higher, visitors stay longer, and organic traffic converts.
          </p>
          <div className="wd-mod-tags" style={{ marginBottom: '36px' }}>
            {['Technical SEO', 'Core Web Vitals', 'Schema Markup', 'Page Speed', 'Keyword Architecture', 'Search Console'].map((t, i) => <span key={i} className="wd-mod-tag">{t}</span>)}
          </div>
          <div className="wd-btn-group">
            <button className="wd-btn-primary">Optimize my website →</button>
            <button className="wd-btn-secondary">Get a free SEO audit</button>
          </div>
        </motion.div>
        <motion.div className="wd-svchero-card" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <div className="wd-svchero-card-hdr">
            <div><div className="wd-svchero-hdr-text">SEO & Performance Dashboard</div><div className="wd-svchero-hdr-sub">6-month progress snapshot</div></div>
            <div className="wd-svchero-hdr-icon">📈</div>
          </div>
          {[
            { icon: '📊', label: 'Organic Sessions',      val: '+184% growth' },
            { icon: '🔑', label: 'Keywords on Page 1',    val: '47 keywords' },
            { icon: '⚡', label: 'PageSpeed Score',        val: '97 / 100' },
            { icon: '📐', label: 'Core Web Vitals',       val: 'All passing ✓' },
            { icon: '🔗', label: 'Backlinks Earned',      val: '124 domains' },
          ].map((r, i) => (
            <div key={i} className="wd-svchero-row">
              <span className="wd-svchero-row-label">{r.icon} {r.label}</span>
              <span className="wd-svchero-row-val">{r.val}</span>
            </div>
          ))}
          <div className="wd-svchero-metric">
            <div><div className="wd-svchero-metric-label">Organic Revenue Attributed</div><div className="wd-svchero-metric-value">$42,800/mo</div></div>
            <div className="wd-svchero-metric-badge">↑ 6-month result</div>
          </div>
        </motion.div>
      </div>
    </section>

    <section className="wd-mod">
      <div className="wd-mod-inner">
        <div className="wd-mod-split">
          <div>
            <div className="wd-mod-section">
              <h3>SEO that compounds over time</h3>
              <p className="wd-mod-split-body">
                Unlike paid ads that stop the moment you stop paying, SEO compounds. A well-optimized page
                that ranks #1 for a high-intent keyword can generate consistent inbound leads for years.
                We build that foundation — technically sound, content-rich, and properly structured — then
                monitor and improve it every month so your rankings grow continuously.
              </p>
              <div className="wd-mod-check-list">
                {[
                  'Full technical SEO audit — crawlability, indexation, sitemaps, robots.txt, canonicals',
                  'Core Web Vitals optimization (LCP, CLS, FID) to meet Google\'s page experience signals',
                  'Keyword research and on-page architecture mapping every page to a target query cluster',
                  'Schema markup implementation — LocalBusiness, FAQPage, Product, Article, and more',
                  'Search Console and GA4 setup with custom conversion tracking and organic dashboards',
                  'Monthly reporting with keyword ranking movement, traffic trends, and action items',
                ].map((item, i) => (
                  <div key={i} className="wd-mod-check">
                    <span className="wd-mod-check-icon">✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <div className="wd-mod-right-box">
              <h4>SEO Optimization Scope</h4>
              {[
                { icon: '🔍', label: 'Technical Audit',          val: 'Full site' },
                { icon: '🔑', label: 'Keyword Research',         val: '200+ keywords' },
                { icon: '📐', label: 'Core Web Vitals',         val: 'All signals' },
                { icon: '🏷️', label: 'Schema Markup',           val: 'All page types' },
                { icon: '🗺️', label: 'XML Sitemap',             val: 'Generated + submitted' },
                { icon: '📊', label: 'Monthly Reporting',        val: 'Rankings + traffic' },
              ].map((r, i) => (
                <div key={i} className="wd-mod-right-row">
                  <span className="wd-mod-right-label">{r.icon} {r.label}</span>
                  <span className="wd-mod-right-val">{r.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Full-stack SEO and performance work</h3>
          <div className="wd-mod-cards">
            {[
              { chip: 'Technical SEO',    title: 'Site Architecture & Crawlability',  body: 'We audit and fix every crawlability issue — broken internal links, orphaned pages, duplicate content, incorrect canonicals, and pagination problems that prevent Google from fully indexing your site.' },
              { chip: 'Core Web Vitals',  title: 'Page Experience Optimization',      body: 'LCP, CLS, and FID optimization across your highest-traffic pages — image sizing, render-blocking resource elimination, layout stability fixes, and interactivity improvements.' },
              { chip: 'On-Page SEO',      title: 'Keyword & Content Architecture',    body: 'Every page mapped to a target keyword cluster with optimized title tags, meta descriptions, headers, and internal linking structures that signal topical authority to search engines.' },
              { chip: 'Schema',           title: 'Structured Data Implementation',    body: 'Rich results-eligible schema markup across all relevant page types — FAQ, reviews, breadcrumbs, products, local business, and article schemas that increase SERP real estate.' },
              { chip: 'Speed',            title: 'PageSpeed & Performance Tuning',    body: 'Image compression and next-gen format conversion, JavaScript deferral, CSS minification, server response time reduction, and browser caching configuration for maximum Lighthouse scores.' },
              { chip: 'Local SEO',        title: 'Local Search Optimization',         body: 'For location-based businesses, we optimize Google Business Profile, implement LocalBusiness schema, build citation consistency, and create geo-targeted landing pages for each service area.' },
            ].map((c, i) => (
              <div key={i} className="wd-mod-card">
                <div className="wd-mod-card-chip">{c.chip}</div>
                <h4>{c.title}</h4>
                <p>{c.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Real-world results</h3>
          <div className="wd-mod-scenarios">
            {[
              { label: 'Organic Growth',  title: '+184% organic traffic in 6 months',              body: 'A B2B SaaS company with zero SEO investment started a technical SEO + content program with BizServe. In 6 months, organic sessions grew 184% and 12 new keywords ranked on page 1 for high-intent queries.' },
              { label: 'Local SEO',       title: 'Law firm went from page 4 to #1 local result',   body: 'A personal injury law firm was invisible in local search. After Local SEO optimization, Google Business Profile management, and location-specific landing pages, they reached the #1 map pack position in 90 days.' },
              { label: 'Speed Impact',    title: 'PageSpeed fix reduced bounce rate by 31%',        body: 'A retail brand\'s mobile PageSpeed score was 34. After a Core Web Vitals overhaul, it reached 91 — and their mobile bounce rate dropped 31%, while mobile revenue increased 44% in the following quarter.' },
            ].map((s, i) => (
              <div key={i} className="wd-mod-scenario">
                <div className="wd-mod-scenario-label">{s.label}</div>
                <h4>{s.title}</h4>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-section">
          <h3>Frequently asked questions</h3>
          <div className="wd-mod-faq">
            {[
              { q: 'How long does SEO take to show results?',      a: 'Technical SEO fixes (broken links, speed, indexation) show results within 2–6 weeks. Keyword ranking improvements typically begin appearing in 2–4 months and grow steadily over 6–12 months as Google processes the changes.' },
              { q: 'Do you offer ongoing SEO or just a one-time fix?', a: 'Both. We offer one-time technical SEO projects for businesses that need a foundation set right, and ongoing monthly SEO retainers for businesses that want continuous ranking improvement, content strategy, and link building.' },
              { q: 'Can you guarantee page 1 rankings?',            a: 'No one can guarantee specific rankings — Google\'s algorithm is too complex for guarantees. What we guarantee is that every optimization we make follows Google\'s guidelines, is transparently documented, and is measurable.' },
              { q: 'What tools do you use for SEO reporting?',      a: 'We report through Google Search Console and GA4 as primary data sources, supplemented by Ahrefs for keyword and backlink tracking. Every client gets a live dashboard and a monthly written report with clear ranking and traffic trends.' },
            ].map((f, i) => (
              <div key={i} className="wd-mod-faq-item">
                <h4>{f.q}</h4>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wd-mod-cta">
          <div className="wd-mod-cta-glow" />
          <h2>Your competitors are already on page 1. Let's get you there.</h2>
          <p>Get a free SEO audit — we'll analyze your site, show you exactly what's holding your rankings back, and map a clear path to page 1.</p>
          <div className="wd-mod-cta-btns">
            <button className="wd-mod-cta-primary">Get my free SEO audit →</button>
            <button className="wd-mod-cta-secondary">Schedule a consultation</button>
          </div>
        </div>
      </div>
    </section>
  </>
);

/* ─────────────────────────────────────────
   MAIN OVERVIEW PAGE
───────────────────────────────────────── */
interface OverviewProps { onSelectService: (slug: string) => void; }

const WebsiteDevelopmentOverview: React.FC<OverviewProps> = ({ onSelectService }) => (
  <>
    {/* ── Hero ── */}
    <section className="wd-hero">
      <div className="wd-hero-blob1" />
      <div className="wd-hero-blob2" />
      <div className="wd-hero-inner">
        <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65 }}>
          <div className="wd-badge">
            <span className="wd-badge-dot" />
            Professional Website Development
          </div>
          <h1>
            Websites That <span className="wd-gradient-text">Work as Hard</span> as You Do.
          </h1>
          <p className="wd-hero-sub">
            We design, build, and optimize websites that attract the right visitors, communicate your
            value instantly, and convert at rates your competitors envy. From custom design to e-commerce,
            CMS builds to SEO — full-service web development under one roof.
          </p>
          <div className="wd-btn-group">
            <button className="wd-btn-primary">Start your project →</button>
            <button className="wd-btn-secondary">View our portfolio</button>
          </div>
        </motion.div>

        <motion.div
          className="wd-hero-visual"
          style={{ position: 'relative' }}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.75, delay: 0.15 }}
        >
          <div className="wd-browser">
            <div className="wd-browser-bar">
              <div className="wd-browser-dots">
                <div className="wd-browser-dot red" />
                <div className="wd-browser-dot yellow" />
                <div className="wd-browser-dot green" />
              </div>
              <div className="wd-browser-url">https://yourclient.com</div>
            </div>
            <div className="wd-browser-body">
              <div className="wd-code-block">
                <div className="wd-code-line">
                  <span className="wd-code-tag">&lt;section</span>
                  <span className="wd-code-attr"> className=</span>
                  <span className="wd-code-val">"hero"</span>
                  <span className="wd-code-tag">&gt;</span>
                </div>
                <div className="wd-code-line">
                  <div className="wd-code-indent" />
                  <span className="wd-code-tag">&lt;h1&gt;</span>
                  <span style={{ fontSize: 12, color: '#e2e8f0', fontFamily: 'monospace' }}>Your Brand, Perfected</span>
                  <span className="wd-code-tag">&lt;/h1&gt;</span>
                </div>
                <div className="wd-code-line">
                  <div className="wd-code-indent" />
                  <span className="wd-code-attr">performance=</span>
                  <span className="wd-code-val">"96/100"</span>
                  <span className="wd-code-attr"> mobile=</span>
                  <span className="wd-code-val">"first"</span>
                </div>
                <div className="wd-code-line">
                  <span className="wd-code-tag">&lt;/section&gt;</span>
                </div>
              </div>
              <div className="wd-metric-row">
                <div className="wd-metric-chip">
                  <div className="wd-mc-val">96</div>
                  <div className="wd-mc-label">PageSpeed</div>
                </div>
                <div className="wd-metric-chip cyan">
                  <div className="wd-mc-val">1.2s</div>
                  <div className="wd-mc-label">Load Time</div>
                </div>
                <div className="wd-metric-chip green">
                  <div className="wd-mc-val">4.8%</div>
                  <div className="wd-mc-label">CVR</div>
                </div>
              </div>
              <div className="wd-deploy-banner">
                <span className="wd-deploy-text">✓ Deployed to production — yourclient.com</span>
                <span className="wd-deploy-badge">Live</span>
              </div>
            </div>
          </div>
          <div className="wd-float-badge">
            <div className="wd-fb-icon">🚀</div>
            <div>
              <div className="wd-fb-label">Delivered on time</div>
              <div className="wd-fb-value">3-week build</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>

    {/* ── Stats Bar ── */}
    <section className="wd-stats">
      <div className="wd-stats-grid">
        {[
          { num: '300+',  label: 'Websites Launched' },
          { num: '96',    label: 'Avg. PageSpeed Score' },
          { num: '4.6×',  label: 'Avg. Conversion Lift Post-Redesign' },
          { num: '98%',   label: 'Client Retention Rate' },
        ].map((s, i) => (
          <motion.div key={i} className="wd-stat-item" {...stagger(i)}>
            <div className="wd-stat-num">{s.num}</div>
            <div className="wd-stat-label">{s.label}</div>
          </motion.div>
        ))}
      </div>
    </section>

    {/* ── Services ── */}
    <section className="wd-section wd-services-bg">
      <div className="wd-container">
        <motion.div className="wd-section-header" {...fadeUp}>
          <div className="wd-kicker">Our Services</div>
          <h2>Every Web Development Service Your Business Needs</h2>
          <p>
            From first design concept to post-launch SEO and ongoing maintenance — we handle the
            full website lifecycle so you can focus on running your business.
          </p>
        </motion.div>
        <div className="wd-services-grid">
          {services.map((svc, i) => (
            <motion.div
              key={i}
              className={`wd-svc-card ${svc.color}`}
              {...stagger(i)}
              onClick={() => onSelectService(svc.slug)}
            >
              <div className={`wd-svc-icon ${svc.color}`}>{svc.icon}</div>
              <h3>{svc.title}</h3>
              <p>{svc.description}</p>
              <div className="wd-svc-tags">
                {svc.tags.map((tag, j) => <span key={j} className="wd-svc-tag">{tag}</span>)}
              </div>
              <div className="wd-svc-arrow">Learn more →</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Feature Spotlight ── */}
    <section className="wd-section wd-spotlight-bg">
      <div className="wd-container">
        <div className="wd-spotlight-inner">
          <div className="wd-spotlight-left">
            <div className="wd-kicker">Why It Matters</div>
            <h2>Your website is your highest-leverage sales asset.</h2>
            <p>
              For most businesses, the website is the first and last chance to make an impression.
              Visitors decide to stay or leave in under 3 seconds. We build sites that earn those
              3 seconds — and convert the sessions that follow.
            </p>
            <div className="wd-feat-list">
              {[
                { color: 'c-violet', icon: '⚡', title: 'Performance-First Engineering',    desc: 'Every site we build targets 90+ PageSpeed scores by default — with optimized images, minimal JavaScript, and CDN delivery baked into the architecture from day one.' },
                { color: 'c-cyan',   icon: '📱', title: 'Mobile-First, Always',             desc: 'Over 60% of web traffic is on mobile. We design for the small screen first and scale up — ensuring a flawless experience regardless of how visitors find you.' },
                { color: 'c-teal',   icon: '🎯', title: 'Built Around Your Conversion Goal', desc: 'Whether the goal is a phone call, a form submission, or a purchase, every design and copy decision on your site is engineered to move visitors toward that outcome.' },
              ].map((f, i) => (
                <div key={i} className="wd-feat">
                  <div className={`wd-feat-icon ${f.color}`}>{f.icon}</div>
                  <div>
                    <h4>{f.title}</h4>
                    <p>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="wd-spotlight-right">
            <div className="wd-visual-card">
              <div className="wd-visual-dots" />
              <div className="wd-visual-metric-box">
                <div className="wd-vm-label">Monthly Organic Traffic</div>
                <div className="wd-vm-value">28,400</div>
                <div className="wd-vm-delta">
                  <span>↑ +184%</span> vs 6 months ago
                </div>
              </div>
              <div className="wd-visual-insight">
                <div className="wd-vi-header">
                  <span className="wd-vi-title">Conversion Funnel</span>
                  <span className="wd-vi-badge">LIVE</span>
                </div>
                {[
                  { label: 'Visitors',    pct: 100, fill: 'linear-gradient(90deg,#6d28d9,#7c3aed)', val: '28.4k' },
                  { label: 'Engaged',     pct: 68,  fill: 'linear-gradient(90deg,#0ea5e9,#06b6d4)', val: '19.3k' },
                  { label: 'Leads',       pct: 38,  fill: 'linear-gradient(90deg,#0d9488,#059669)', val: '10.8k' },
                  { label: 'Converted',   pct: 14,  fill: 'linear-gradient(90deg,#f59e0b,#d97706)', val: '3,976' },
                ].map((bar, i) => (
                  <div key={i} className="wd-bar-row">
                    <span className="wd-bar-label">{bar.label}</span>
                    <div className="wd-bar-track">
                      <div className="wd-bar-fill" style={{ width: `${bar.pct}%`, background: bar.fill }} />
                    </div>
                    <span className="wd-bar-val">{bar.val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── Process ── */}
    <section className="wd-section wd-process-bg">
      <div className="wd-container">
        <motion.div className="wd-section-header" {...fadeUp}>
          <div className="wd-kicker">Our Process</div>
          <h2>From Brief to Live in 5 Proven Steps</h2>
          <p>
            We follow a transparent, milestone-driven process that keeps you informed at every stage —
            from strategy session to launch day and beyond.
          </p>
        </motion.div>
        <div className="wd-steps">
          {processSteps.map((step, i) => (
            <motion.div key={i} className="wd-step" {...stagger(i)}>
              <div className="wd-step-num">{step.num}</div>
              <div className="wd-step-chip">{step.chip}</div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Why Us (dark) ── */}
    <section className="wd-section wd-why-bg">
      <div className="wd-why-glow1" />
      <div className="wd-why-glow2" />
      <div className="wd-container">
        <motion.div className="wd-section-header" {...fadeUp}>
          <div className="wd-kicker">Why BizServe</div>
          <h2>Built Different. Delivered Better.</h2>
          <p>
            We've launched 300+ websites for businesses across every industry. Here's what makes the
            BizServe approach worth the investment.
          </p>
        </motion.div>
        <div className="wd-why-grid">
          {whyItems.map((item, i) => (
            <motion.div key={i} className="wd-why-card" {...stagger(i)}>
              <span className="wd-why-icon-wrap">{item.icon}</span>
              <h4>{item.title}</h4>
              <p>{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Testimonials ── */}
    <section className="wd-section wd-testimonials-bg">
      <div className="wd-container">
        <motion.div className="wd-section-header" {...fadeUp}>
          <div className="wd-kicker">Client Results</div>
          <h2>Websites That Moved the Needle</h2>
          <p>
            Don't take our word for it. Here's what businesses say after partnering with BizServe
            to rebuild and optimize their web presence.
          </p>
        </motion.div>
        <div className="wd-testimonials-grid">
          {testimonials.map((t, i) => (
            <motion.div key={i} className="wd-tcard" {...stagger(i)}>
              <div className="wd-stars">★★★★★</div>
              <p className="wd-tcard-text">{t.text}</p>
              <div className="wd-tcard-author">
                <div className="wd-author-avatar" style={{ background: t.color }}>{t.initials}</div>
                <div>
                  <div className="wd-author-name">{t.name}</div>
                  <div className="wd-author-role">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ── CTA ── */}
    <section className="wd-section wd-cta-bg">
      <div className="wd-container">
        <motion.div className="wd-cta-inner" {...fadeUp}>
          <div className="wd-cta-glow1" />
          <div className="wd-cta-glow2" />
          <h2>Your best-performing website<br />starts with one conversation.</h2>
          <p>Tell us about your project — we'll scope it, price it, and show you exactly what a BizServe-built website will do for your business.</p>
          <div className="wd-cta-btns">
            <button className="wd-cta-primary">Start my project today →</button>
            <button className="wd-cta-secondary">Schedule a free consultation</button>
          </div>
          <div className="wd-cta-trust">
            {['Fixed-price projects', 'No surprise invoices', 'On-time delivery guarantee', 'Cancel anytime'].map((item, i) => (
              <div key={i} className="wd-cta-trust-item">
                <span className="check">✓</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  </>
);

/* ─────────────────────────────────────────
   ROUTER SHELL
───────────────────────────────────────── */
const websiteDevServicePages: Record<string, React.FC<ServicePageProps>> = {
  'custom-design':      CustomDesignPage,
  'ecommerce-dev':      EcommercePage,
  'landing-pages':      LandingPagesPage,
  'cms-wordpress':      CMSWordPressPage,
  'maintenance-support': MaintenancePage,
  'seo-performance':    SEOPerformancePage,
};

const WebsiteDevelopment: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const serviceSlug = (searchParams.get('service') || '').toLowerCase();

  const handleSelectService = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('service', slug);
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('service');
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const ServicePage = serviceSlug ? websiteDevServicePages[serviceSlug] : null;

  return (
    <div className="wd-page">
      <style>{styles}</style>
      {ServicePage
        ? <ServicePage onBack={handleBack} />
        : <WebsiteDevelopmentOverview onSelectService={handleSelectService} />
      }
    </div>
  );
};

export default WebsiteDevelopment;
